// ============================================
// grokActions.js - runs settings actions for the chat assistant.
// window CustomEvent 'cheddar:settings' { op: 'open'|'set'|'flash', id, menu, value, reply? }
// Actions run ONE AT A TIME (~400ms apart) so the user can watch:
//   set  -> validate (registry) -> confirm card if def.confirm -> open menu
//           -> wait for the row -> flash it -> apply -> settle
// Each result is passed to detail.reply (if a function) and re-broadcast as
// 'cheddar:settings-result'. Debug: window.__cheddarCtl { list, get, set, open, flash }.
// ============================================
import { getDef, getValue, validateValue, applyValue, listSettings, openMenu, isBlocked, formatValue } from './settingsRegistry'
import { activeChartBridge } from './grokDraw'
import { registerGrokAction, runGrokAction, listGrokActions } from './grokRegistry'
import { signalSnapshot } from './signalBus'              // grok-orders-20261021
import { recipesForContext } from './orderRecipes'        // also registers the recipe actions

// ─── Drawing actions (registry; grok-generic-20261016) ───
// Legacy ops (draw / list_drawings / remove_drawing / clear_grok_drawings)
// sent by the old server tools dispatch to these same handlers. Only
// draw_rect is advertised; the others stay hidden because the server still
// has dedicated tools for them.
const withBridge = (fn) => async (args) => {
  const b = activeChartBridge()
  if (!b) return { ok: false, error: 'no active chart' }
  return fn(b, args || {})
}
registerGrokAction('chart_draw', { hidden: true, handler: withBridge((b, a) => b.draw(a)) })
registerGrokAction('chart_list_drawings', { hidden: true, handler: withBridge((b) => ({ ok: true, drawings: b.list() })) })
registerGrokAction('chart_clear_grok_drawings', { hidden: true, handler: withBridge((b) => b.clearGrok()) })
registerGrokAction('chart_remove_drawing', {
  hidden: true,
  handler: withBridge(async (b, a) => {
    const d = b.info(a.id)
    if (!d) return { id: a.id, ok: false, error: 'no drawing with that id' }
    if (d.by !== 'grok') {
      const yes = await requestConfirm({ title: 'Remove drawing', text: `Remove your ${d.kind === 'sr' ? 'S/R line' : d.kind === 'rect' ? 'rectangle' : d.kind}${d.price ? ' at ' + d.price : ''}?` })
      if (!yes) return { id: a.id, ok: false, error: 'declined by user' }
    }
    return b.remove(a.id)
  }),
})
const T = { type: 'integer', description: 'candle open time, epoch ms (from chart.view)' }
const AGO = { type: 'integer', description: 'alternative to time: candles back from the latest (0 = latest, negative = future)' }
registerGrokAction('draw_rect', {
  description: 'Draw a rectangle (zone/range box) on the active chart between two corners. Each corner is time or bars_ago plus a price (chart y-units). Corner 2 may be up to 200 candles in the future. Removable via remove_drawing (id tl:...).',
  params: {
    type: 'object',
    properties: {
      time1: T, bars_ago1: AGO, price1: { type: 'number' },
      time2: T, bars_ago2: AGO, price2: { type: 'number' },
      color: { type: 'string', description: '#rrggbb' },
      fill_opacity: { type: 'number', description: '0..1, default 0.15' },
      label: { type: 'string', description: 'max 24 chars' },
    },
    required: ['price1', 'price2'],
  },
  handler: withBridge((b, a) => b.draw({
    kind: 'rect', time1: a.time1, bars_ago1: a.bars_ago1, price1: a.price1,
    time2: a.time2, bars_ago2: a.bars_ago2, price2: a.price2,
    color: a.color, fill_opacity: a.fill_opacity, label: a.label,
  })),
})

export { listGrokActions }

/** Compact context of the active chart for the Grok snapshot (or null). */
export function chartContext() {
  try {
    const c = activeChartBridge()?.context() || null
    if (!c) return null
    // grok-orders-20261021: live indicator snapshot + saved order recipes ride along.
    let ind = null; try { ind = signalSnapshot() } catch {}
    let rec = []; try { rec = recipesForContext().slice(0, 50) } catch {}
    return { ...c, indicators: ind || undefined, order_recipes: rec }
  } catch { return null }
}

const SPACING_MS = 400
const FLASH_MS = 1300
const sleep = (ms) => new Promise(r => setTimeout(r, ms))

function waitForEl(selector, timeoutMs = 900) {
  return new Promise((resolve) => {
    const t0 = performance.now()
    const tick = () => {
      const el = document.querySelector(selector)
      if (el) return resolve(el)
      if (performance.now() - t0 > timeoutMs) return resolve(null)
      requestAnimationFrame(tick)
    }
    tick()
  })
}

const cssEsc = (s) => (window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&'))

export async function flashSetting(highlightId) {
  const el = await waitForEl(`[data-setting-id="${cssEsc(highlightId)}"]`)
  if (!el) return false
  try { el.scrollIntoView({ block: 'nearest' }) } catch {}
  el.classList.remove('cm-flash')
  void el.offsetWidth            // restart the animation
  el.classList.add('cm-flash')
  setTimeout(() => el.classList.remove('cm-flash'), FLASH_MS)
  return true
}

/** Ask the user (SettingsConfirmCard). Resolves false if nobody answers. */
export function requestConfirm({ title, text }) {
  return new Promise((resolve) => {
    let done = false
    const finish = (v) => { if (!done) { done = true; clearTimeout(to); resolve(!!v) } }
    const to = setTimeout(() => finish(false), 60000)
    window.dispatchEvent(new CustomEvent('cheddar:confirm', { detail: { title, text, resolve: finish } }))
  })
}

/** Tour step (TourSpotlight). Resolves 'next' | 'close' | 'missing'. */
export function requestTourStep({ panel, caption, index, total }) {
  return new Promise((resolve) => {
    let done = false
    const finish = (v) => { if (!done) { done = true; clearTimeout(to); resolve(v) } }
    const to = setTimeout(() => finish('next'), 30000)
    window.dispatchEvent(new CustomEvent('cheddar:tour', { detail: { panel, caption, index, total, resolve: finish } }))
  })
}

/** Panels the tour can spotlight (data-tour-id), for the Grok snapshot. */
export function listPanels() {
  const seen = new Map()
  document.querySelectorAll('[data-tour-id]').forEach(el => {
    const id = el.getAttribute('data-tour-id')
    if (!id) return
    const r = el.getBoundingClientRect()
    const vis = r.width > 0 && r.height > 0
    const prev = seen.get(id)
    if (!prev || (!prev.visible && vis)) seen.set(id, { id, label: el.getAttribute('data-tour-label') || id, visible: vis })
  })
  return [...seen.values()]
}

let tourClosed = false
async function runOne(a) {
  const op = a?.op
  if (op === 'highlight') {
    if (tourClosed) return { op, panel: a.panel, ok: false, error: 'tour closed' }
    const r = await requestTourStep({ panel: a.panel, caption: a.caption, index: a._i, total: a._n })
    if (r === 'close') tourClosed = true
    return { op, panel: a.panel, ok: r !== 'missing', result: r }
  }
  if (op === 'open') {
    const ok = openMenu(a.menu || getDef(a.id)?.menu)
    return { op, menu: a.menu, ok, error: ok ? undefined : 'no such menu here' }
  }
  if (op === 'flash') {
    const def = getDef(a.id)
    if (def?.menu) { openMenu(def.menu); await sleep(120) }
    const ok = await flashSetting(def?.highlight || a.id)
    return { op, id: a.id, ok }
  }
  if (op === 'set') {
    const id = a.id
    if (isBlocked(id)) return { op, id, ok: false, error: 'blocked' }
    const def = getDef(id)
    if (!def) return { op, id, ok: false, error: 'unknown setting' }
    const v = validateValue(def, a.value)
    if (!v.ok) return { op, id, ok: false, error: v.error }
    const from = getValue(id)
    if (from === v.value) return { op, id, ok: true, value: v.value, unchanged: true }
    if (def.confirm) {
      const yes = await requestConfirm({
        title: def.group,
        text: `${def.label}: ${formatValue(def, from)} \u2192 ${formatValue(def, v.value)}?`,
      })
      if (!yes) return { op, id, ok: false, error: 'declined by user' }
    }
    if (def.menu) { openMenu(def.menu); await sleep(120) }
    await flashSetting(def.highlight || id)
    const r = applyValue(id, v.value)
    return { op, id, from, value: v.value, ...r }
  }
  // Generic registry action (server tool app_action).
  if (op === 'app_action') return { op, action: a.action, ...(await runGrokAction(a.action, a.args)) }
  // Legacy drawing ops -> same registry handlers.
  if (op === 'draw') return { op, ...(await runGrokAction('chart_draw', a)) }
  if (op === 'list_drawings') return { op, ...(await runGrokAction('chart_list_drawings', a)) }
  if (op === 'clear_grok_drawings') return { op, ...(await runGrokAction('chart_clear_grok_drawings', a)) }
  if (op === 'remove_drawing') return { op, ...(await runGrokAction('chart_remove_drawing', a)) }
  return { op, ok: false, error: 'unsupported op' }
}

const queue = []
let running = false
async function pump() {
  if (running) return
  running = true
  while (queue.length) {
    const { action, reply } = queue.shift()
    let res
    try { res = await runOne(action) } catch (e) { res = { op: action?.op, ok: false, error: String(e?.message || e) } }
    try { if (typeof reply === 'function') reply(res) } catch {}
    window.dispatchEvent(new CustomEvent('cheddar:settings-result', { detail: res }))
    if (queue.length) await sleep(SPACING_MS)
  }
  running = false
  tourClosed = false
}

/** Run a batch from Grok in order; resolves with every result. */
export function runActions(actions) {
  const list = Array.isArray(actions) ? actions.slice(0, 24) : []
  const hl = list.filter(a => a && a.op === 'highlight')
  let k = 0
  for (const a of list) if (a && a.op === 'highlight') { a._i = k++; a._n = hl.length }
  return Promise.all(list.map(a => enqueue(a)))
}

export function enqueue(action) {
  return new Promise((resolve) => { queue.push({ action, reply: resolve }); pump() })
}

let installed = false
export function installGrokActions() {
  if (installed || typeof window === 'undefined') return
  installed = true
  window.addEventListener('cheddar:settings', (e) => {
    const d = e?.detail || {}
    queue.push({ action: d, reply: d.reply }); pump()
  })
  window.__cheddarCtl = {
    list: (group) => listSettings(group),
    get: (id) => getValue(id),
    set: (id, value) => enqueue({ op: 'set', id, value }),
    open: (menu) => enqueue({ op: 'open', menu }),
    flash: (id) => enqueue({ op: 'flash', id }),
    tour: (panel, caption) => enqueue({ op: 'highlight', panel, caption }),
    panels: () => listPanels(),
    run: (actions) => runActions(actions),
    draw: (a) => enqueue({ op: 'draw', ...a }),
    drawings: () => enqueue({ op: 'list_drawings' }),
    chart: () => chartContext(),
    actions: () => listGrokActions(),
    action: (name, args) => enqueue({ op: 'app_action', action: name, args }),
  }
}